/**
 * First-touch acquisition attribution.
 *
 * On a visitor's very first page load we record where they landed, where
 * they came from (document.referrer) and any utm_* parameters, and keep that
 * bundle in localStorage. It is sent to the backend exactly once — with the
 * registration call — so the student profile can answer "how did this
 * student find us?" without depending on server access logs.
 *
 * First-party, functional, never read by a third party. Everything is
 * best-effort: storage may be blocked (private mode, prerender) and the
 * signup must work regardless.
 */

export interface Attribution {
  landing_page: string;
  referrer: string;
  first_visit_at: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
}

const STORAGE_KEY = 'hudan_first_touch';
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const;
const MAX_LEN = 500;

function clip(value: string, max = MAX_LEN): string {
  return value.length > max ? value.slice(0, max) : value;
}

/** document.referrer unless it is this site itself (reload, in-app link). */
function externalReferrer(): string {
  const ref = document.referrer || '';
  if (!ref) return '';
  try {
    return new URL(ref).hostname === window.location.hostname ? '' : ref;
  } catch {
    return ref;
  }
}

function readStored(): Attribution | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<Attribution>;
    if (!parsed || typeof parsed.landing_page !== 'string') return null;
    return parsed as Attribution;
  } catch {
    return null;
  }
}

/**
 * Record the first touch if none is stored yet. Idempotent; call once at
 * app boot, before React renders, so the landing URL is the real entry point
 * and not a route the app redirected to.
 */
export function captureFirstTouch(): void {
  if (typeof window === 'undefined') return;
  try {
    if (readStored()) return;

    const { pathname, search } = window.location;
    const params = new URLSearchParams(search);
    const record: Attribution = {
      landing_page: clip(pathname + search),
      referrer: clip(externalReferrer()),
      first_visit_at: new Date().toISOString(),
    };
    for (const key of UTM_KEYS) {
      const value = params.get(key);
      if (value) record[key] = clip(value.trim(), 140);
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch {
    // storage blocked — attribution is optional
  }
}

/** The stored first touch, or null when nothing was captured. */
export function getAttribution(): Attribution | null {
  return readStored();
}

/**
 * The first touch serialised for a Frappe call (the API accepts a JSON
 * string), or undefined so the key is omitted entirely when unknown.
 */
export function getAttributionPayload(): string | undefined {
  const record = readStored();
  return record ? JSON.stringify(record) : undefined;
}
